import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { angka, rupiah } from "../lib/format";
import { pesanGalatPembeli } from "../lib/galatPembeli";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toastGalatKeranjang, batasPcsDariGalat } from "../lib/batasPcs";
import { TEKS_WA, tautanWa } from "./wa";
import { tampilkanToastHp } from "./ToastHp";
import { asalNavigasiHp } from "./navigasiHp";
import { chatAktif, bukaChat } from "../chat/chatState";

// Tambah ke keranjang dari HP: belum masuk → halaman Masuk (kembali ke produk sesudahnya), berhasil → toast HP, galat → toast sonner lama.
// POST /api/cart/items MENGGANTI jumlah. Tombol "+" kartu (tambahKartu) menambah di atas jumlah yang sudah ada di keranjang supaya "+"
// tidak pernah menurunkan jumlah (review S1); halaman produk tetap mengganti jumlah seperti tampilan komputer.
// PR harga kartu HP (papan "Sesudah tekan +"): toast tombol + = "1.000 pcs ditambahkan" / "500 pcs ditambahkan (total 1.500 pcs)" + baris
// "Diskon 5% · Rp465/pc" dari item produk itu di JAWABAN server (harga yang berlaku di keranjang); jawaban tanpa item → baris 2 tidak tampil.
export function toastKartu(p, ditambah, total, ringkasan) {
  let it = null;
  for (const c of ringkasan?.categories || []) for (const x of c.items || []) if (x.produk_id === p.id) it = x;
  const harga = it && it.harga_efektif !== undefined ? `${rupiah(it.harga_efektif)}/pc` : null;
  return { sr: p.nama, baris1: `${angka(ditambah)} pcs ditambahkan${total ? ` (total ${angka(total)} pcs)` : ""}`,
    baris2: harga ? (it.persen_diskon > 0 ? `Diskon ${it.persen_diskon}% · ${harga}` : harga) : null };
}
export function useTambah() {
  const { user } = useAuth();
  const { addItem, summary } = useCart();
  const nav = useNavigate();
  const lokasi = useLocation();
  const [sibuk, setSibuk] = useState(null);   // id produk yang sedang dikirim

  const tambah = async (p, qty, { denganJumlah = false, kartu = null } = {}) => {
    const href = `/produk/${p.slug || p.id}`;
    if (!user) { nav("/login", { state: { from: href, ...asalNavigasiHp(lokasi) } }); return; }
    if (sibuk) return;
    setSibuk(p.id);
    try {
      const ringkasan = await addItem(p.id, qty);
      tampilkanToastHp(kartu ? toastKartu(p, kartu.ditambah, kartu.total, ringkasan) : denganJumlah ? `${p.nama} ${angka(qty)} pcs Ditambahkan` : `${p.nama} Ditambahkan`);
    } catch (err) {
      // Batas pcs per pesanan: teks server + tombol WhatsApp "saya ingin pesan <produk> lebih dari N pcs" (butir 4)
      const b = batasPcsDariGalat(err);
      const url = b ? tautanWa(b.nomor_whatsapp, TEKS_WA.pesanBesar(p.nama, b.maks_pcs)) : null;
      if (b) toast.error(b.pesan, { duration: 10000, ...(chatAktif() ? { action: { label: "Chat", onClick: () => bukaChat({ produk_id: p.id }) } } : url ? { action: { label: "Chat WhatsApp", onClick: () => window.open(url, "_blank", "noopener,noreferrer") } } : {}) });
      else toastGalatKeranjang(toast, err, pesanGalatPembeli(err.response?.data?.detail) || "Gagal menambahkan ke keranjang");
    } finally {
      setSibuk(null);
    }
  };
  // HP-4 Beli Sekarang (papan B2 → B4d): isi keranjang dengan jumlah halaman (sama dengan Tambah ke Keranjang), lalu checkout produk itu
  // saja (/checkout?beli=<id>, server `hanya`); isi keranjang lain tetap. Galat (batas pcs, stok) = sama dengan tambah, tanpa pindah halaman.
  const beli = async (p, qty) => {
    const href = `/produk/${p.slug || p.id}`;
    if (!user) { nav("/login", { state: { from: href, ...asalNavigasiHp(lokasi) } }); return; }
    if (sibuk) return;
    setSibuk(p.id);
    // PR #42 (review Codex): asal (termasuk posisi riwayat) dicatat SEBELUM menunggu server; bila pembeli sudah pindah halaman saat
    // jawaban datang, tidak dialihkan ke checkout (asal tidak lagi cocok dengan halaman sekarang).
    const asal = asalNavigasiHp(lokasi);
    const diSini = () => `${window.location.pathname}${window.location.search}${window.location.hash}` === asal.hpKembali.to;
    let ok = false;
    try { await addItem(p.id, qty); ok = true; }
    catch (err) {
      const b = batasPcsDariGalat(err);
      const url = b ? tautanWa(b.nomor_whatsapp, TEKS_WA.pesanBesar(p.nama, b.maks_pcs)) : null;
      if (b) toast.error(b.pesan, { duration: 10000, ...(chatAktif() ? { action: { label: "Chat", onClick: () => bukaChat({ produk_id: p.id }) } } : url ? { action: { label: "Chat WhatsApp", onClick: () => window.open(url, "_blank", "noopener,noreferrer") } } : {}) });
      else toastGalatKeranjang(toast, err, pesanGalatPembeli(err.response?.data?.detail) || "Gagal memproses pembelian");
    } finally { setSibuk(null); }
    if (ok && diSini()) nav(`/checkout?beli=${encodeURIComponent(p.id)}`, { state: asal });
  };
  const diKeranjang = (pid) => {
    for (const c of summary?.categories || []) for (const it of c.items || []) if (it.produk_id === pid) return Number(it.qty_pcs) || 0;
    return 0;
  };
  const tambahKartu = (p) => {
    // jumlah pertama = definisi server (harga_minimum.qty_pcs, server/harga.js), supaya harga di kartu = harga jumlah yang benar-benar dimasukkan;
    // cadangan hanya untuk data produk tanpa harga_minimum (mis. simpanan memori tepat saat tayang)
    const min = p.harga_minimum?.qty_pcs || p.minimum_pcs || 500;
    const ada = diKeranjang(p.id);
    const ditambah = ada > 0 ? (p.kelipatan_pcs || min) : min;
    return tambah(p, ada + ditambah, { kartu: { ditambah, total: ada > 0 ? ada + ditambah : 0 } });
  };
  return { tambah, tambahKartu, beli, sibuk };
}
